/**
 * What this Team reads, both layers in one place (YAG-986). Every workspace
 * connection, read by default, with Hide and Show for whoever manages the
 * Team; and inside Jira, Confluence, Slack, Linear and Notion each project,
 * space or channel with whether this Team's reads reach it (open to the
 * workspace, granted to this Team, or dark) and the one thing the viewer can
 * do about a dark one: open it to the workspace (an admin), grant it to the
 * Team (an admin or the connecting user), or ask an admin.
 *
 * Every act lands at once through the routes that already exist and re-reads
 * the list; nothing here widens anything those routes would refuse.
 */
import { useState } from "react";
import type { TeamReadConnection, TeamReadContainer } from "@yagni/shared";

import { Btn, Chip, Fold, Pill, cx } from "../queue/index.js";
import { captureUiError } from "../sentry.js";
import {
  CONNECTIONS_HELP,
  CONNECTIONS_NONE,
  CONNECTIONS_RETRY,
  CONNECTIONS_UNREAD,
  darkUnnamedLine,
  CONTAINER_ACTION_LABEL,
  CONTAINER_STATE_WORD,
  HIDE_CONNECTION,
  SHOW_CONNECTION,
  connectionLabel,
  connectionReadsFact,
  containerNoun,
  darkWhy,
  hiddenConnectionLine,
  readsSummaryLine,
} from "./copy.js";
import {
  TeamWriteError,
  addTeamConnectedTool,
  grantContainerToTeam,
  hideTeamConnection,
  openContainerToWorkspace,
} from "./teamsApi.js";
import { useTeamReads } from "./useTeamReads.js";

export interface ReadsPanelProps {
  deskId: string;
  /** Nothing may change: the viewer cannot manage the Team, or it is archived, or a save is in flight. */
  disabled?: boolean;
  /** Called after an act landed, so the page can re-read the Team. */
  onChanged?: () => void;
}

function refusalText(err: unknown): string {
  if (err instanceof TeamWriteError) return err.message;
  return "That did not land. Try again.";
}

export function ReadsPanel({ deskId, disabled, onChanged }: ReadsPanelProps) {
  const { reads, error, refetch } = useTeamReads(deskId);
  const [pending, setPending] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  const act = async (key: string, run: () => Promise<void>) => {
    if (pending) return;
    setPending(key);
    setFailed(null);
    try {
      await run();
      await refetch();
      onChanged?.();
    } catch (err) {
      captureUiError(err, {
        area: "Teams",
        operation: "team_reads_act",
        deskId,
        severity: "warning",
      });
      setFailed(refusalText(err));
    } finally {
      setPending(null);
    }
  };

  if (error) {
    // The same surface as a failed act: an alert, with the one way forward.
    return (
      <p className="help err" role="alert" data-testid="team-reads-unread">
        {CONNECTIONS_UNREAD}{" "}
        <Btn size="sm" variant="quiet" onClick={() => void refetch()}>
          {CONNECTIONS_RETRY}
        </Btn>
      </p>
    );
  }
  if (!reads) return null;
  const canManage = reads.canManage && !disabled;
  const hidden = reads.connections.filter((c) => c.hidden).map((c) => c.source);

  return (
    <div className="reads" data-testid="team-reads">
      <p className="help" data-testid="team-reads-summary">
        {reads.connections.length === 0 ? CONNECTIONS_NONE : readsSummaryLine(hidden)}
      </p>
      {reads.connections.length > 0 && (
        <ul className="reads-list">
          {reads.connections.map((connection) => (
            <ConnectionRow
              key={connection.source}
              connection={connection}
              canManage={canManage}
              pending={pending}
              onHide={() =>
                void act(connection.source, () => hideTeamConnection(deskId, connection.source))
              }
              onShow={() =>
                void act(connection.source, () => addTeamConnectedTool(deskId, connection.source))
              }
              onGrant={(container) =>
                void act(container.id, () => grantContainerToTeam(deskId, container.id))
              }
              onOpen={(container) =>
                void act(container.id, () =>
                  openContainerToWorkspace(connection.source, container.id),
                )
              }
            />
          ))}
        </ul>
      )}
      {failed && (
        <p className="help err" role="alert" data-testid="team-reads-failed">
          {failed}
        </p>
      )}
      <p className="help">{CONNECTIONS_HELP}</p>
    </div>
  );
}

function ConnectionRow({
  connection,
  canManage,
  pending,
  onHide,
  onShow,
  onGrant,
  onOpen,
}: {
  connection: TeamReadConnection;
  canManage: boolean;
  pending: string | null;
  onHide: () => void;
  onShow: () => void;
  onGrant: (container: TeamReadContainer) => void;
  onOpen: (container: TeamReadContainer) => void;
}) {
  const label = connectionLabel(connection.source);
  const containers = connection.containers ?? [];
  const busy = pending === connection.source;
  return (
    <li
      className={cx("reads-row", connection.hidden && "hidden")}
      data-testid={`team-reads-${connection.source}`}
    >
      <div className="reads-head">
        <b>{label}</b>
        <span className="fact">{connectionReadsFact(connection)}</span>
        {canManage && (
          <Btn
            size="sm"
            variant="quiet"
            disabled={pending != null}
            onClick={connection.hidden ? onShow : onHide}
            aria-label={`${connection.hidden ? SHOW_CONNECTION : HIDE_CONNECTION} ${label}`}
          >
            {busy ? "…" : connection.hidden ? SHOW_CONNECTION : HIDE_CONNECTION}
          </Btn>
        )}
      </div>
      {connection.hidden && <p className="help">{hiddenConnectionLine(connection.source)}</p>}
      {connection.darkUnnamed > 0 && (
        <p className="help" data-testid={`team-reads-dark-unnamed-${connection.source}`}>
          {darkUnnamedLine(connection.source, connection.darkUnnamed)}
        </p>
      )}
      {containers.length > 0 && (
        <Fold
          title={`${containers.length} ${containerNoun(connection.source, containers.length)}`}
          count={`${containers.filter((c) => c.state !== "dark").length} read`}
        >
          <ul className="reads-containers">
            {containers.map((container) => {
              const why =
                container.state === "dark"
                  ? darkWhy(connection.source, container.privacyClass)
                  : null;
              const containerBusy = pending === container.id;
              return (
                <li
                  key={container.id}
                  data-testid={`team-reads-container-${container.id}`}
                  data-state={container.state}
                >
                  <span className="name">{container.name ?? container.externalId}</span>
                  {container.state === "dark" ? (
                    <Pill kind="stop">{CONTAINER_STATE_WORD.dark}</Pill>
                  ) : (
                    <Chip>{CONTAINER_STATE_WORD[container.state]}</Chip>
                  )}
                  {why && <span className="help">{why}</span>}
                  {/* The server decided who may act on a dark container (an
                      admin, or the person who connected it); the Team's own
                      manage right only governs Hide and Show. */}
                  {container.action === "open_to_workspace" && (
                    <Btn
                      size="sm"
                      variant="secondary"
                      disabled={pending != null}
                      onClick={() => onOpen(container)}
                    >
                      {containerBusy ? "…" : CONTAINER_ACTION_LABEL.open_to_workspace}
                    </Btn>
                  )}
                  {container.action === "grant" && (
                    <Btn
                      size="sm"
                      variant="secondary"
                      disabled={pending != null}
                      onClick={() => onGrant(container)}
                    >
                      {containerBusy ? "…" : CONTAINER_ACTION_LABEL.grant}
                    </Btn>
                  )}
                  {container.action === "ask" && (
                    <span className="help">{CONTAINER_ACTION_LABEL.ask}</span>
                  )}
                </li>
              );
            })}
          </ul>
        </Fold>
      )}
    </li>
  );
}
